/* eslint-disable*/
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API_URL } from "../../config";
import { showToast } from "../../components/toast";
import StateManagedSelect from "react-select";
import Swal from "sweetalert2";
import { Link, useParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHome,
  faArrowUp,
  faPlus,
  faTrash,
  faEye,
} from "@fortawesome/free-solid-svg-icons";
import LoadingSpinner from "../../components/LoadingSpinner";

const PortfolioEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [serviceData, setServiceData] = useState(null);
  const [portfolioSub, setPortfolioSub] = useState([]);
  const [deletedPortfolioSub, setDeletedPortfolioSub] = useState([]);
  const [symbolList, setSymbolList] = useState([]);
  const [assetClassList, setAssetClassList] = useState([]);
  const [deleteReasons, setDeleteReasons] = useState({});
  const [pendingDelete, setPendingDelete] = useState([]);
  const reasonInputRef = useRef(null);
  // Form state
  const [formData, setFormData] = useState({
    rebalance_date: new Date().toISOString().split("T")[0],
    orig_stocks: "",
    orig_weightage: "",
    orig_segment: "",
    orig_asset_class: "",
    orig_price: "",
    orig_add_reason: "",
  });

  // State for modals
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [currentReasonField, setCurrentReasonField] = useState(null);
  const [reasonText, setReasonText] = useState("");
  const [showDeleteReasonModal, setShowDeleteReasonModal] = useState(false);
  const [currentDeleteReason, setCurrentDeleteReason] = useState("");

  // Fetch portfolio & service data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("authToken");

        // Fetch service + portfolio
        const response = await axios.get(`${API_URL}portfolio/edit/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = response.data.data;

        if (data) {
          setServiceData(data);

          // Correctly map the portfoliosub data from API response
          setPortfolioSub(data.portfoliosub || []);

          // Set deleted portfoliosub data - FIXED: using correct property name
          setDeletedPortfolioSub(data.portfoliosubdelete || []);

          // Process symbol list - filter out null values and get unique symbols
          const validSymbols = [
            ...new Set(
              (data.symbollist || []).filter((symbol) => symbol !== null)
            ),
          ];
          setSymbolList(validSymbols);
        }

        // If assetClassList is not in the API response, set default values
        if (data && data.assetClassList) {
          const formattedAssetClasses = data.assetClassList.map((asset) => ({
            value: asset.id,
            label: asset.asset_name,
          }));
          setAssetClassList(formattedAssetClasses);
        } else {
          // Default asset classes if not provided by API
          setAssetClassList([
            { value: 0, label: "Equity" },
            { value: 1, label: "Debt" },
            { value: 2, label: "Commodity" },
            { value: 3, label: "Cash" },
          ]);
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching portfolio data:", err);
        showToast.error("Failed to load portfolio data");
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  useEffect(() => {
    if (showReasonModal && reasonInputRef.current) {
      reasonInputRef.current.focus();
    }
  }, [showReasonModal]);

  // Load additional symbols for dropdown (NSE only) if needed
  useEffect(() => {
    // Only fetch additional symbols if the API didn't provide enough
    if (symbolList.length === 0) {
      const controller = new AbortController();
      (async () => {
        try {
          const token = localStorage.getItem("authToken");
          const params = new URLSearchParams({ limit: 100 });
          const url = `${API_URL}notification/symbol?${params.toString()}`;
          const resp = await axios.get(url, {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          });
          const rows = resp?.data?.data?.data || [];
          const nseSymbols = rows
            .filter((r) => r.exchange === "NSE")
            .map((r) => r.syms)
            .filter(Boolean);
          setSymbolList(nseSymbols);
        } catch (e) {
          if (e.name !== "CanceledError") {
            console.error("Failed to fetch symbols", e);
          }
        }
      })();
      return () => controller.abort();
    }
  }, [symbolList.length]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleStockSelect = async (symbol) => {
    setFormData((prev) => ({ ...prev, orig_stocks: symbol || "" }));

    if (symbol) {
      try {
        const token = localStorage.getItem("authToken");

        // ✅ Correct integration using POST with body instead of GET with query params
        const response = await axios.post(
          `${API_URL}backendApi/get-price`,
          {
            exchange: "NSE",
            symbol: symbol,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        const { last_price } = response.data;

        // ✅ Keep your existing logic untouched
        setFormData((prev) => ({
          ...prev,
          orig_price: last_price || "0",
        }));
      } catch (err) {
        console.error("Error fetching stock price:", err);
        setFormData((prev) => ({ ...prev, orig_price: "0" }));
      }
    }
  };

  const addPortfolioItem = () => {
    const {
      orig_stocks,
      orig_weightage,
      orig_segment,
      orig_asset_class,
      orig_price,
      orig_add_reason,
    } = formData;

    if (!orig_stocks) {
      showToast.error("Stock is required");
      return;
    }

    if (!orig_weightage || orig_weightage <= 0) {
      showToast.error("Valid weightage is required");
      return;
    }

    const selectedAssetClass = assetClassList.find(
      (asset) => asset.value == orig_asset_class
    );
    const assetClassName = selectedAssetClass ? selectedAssetClass.label : "";

    const newItem = {
      id: 0, // 0 indicates new item (not yet saved to DB)
      stocks: orig_stocks.toUpperCase(),
      weightage: parseFloat(orig_weightage),
      portfolio_segment: orig_segment,
      asset_class: parseInt(orig_asset_class) || 0,
      asset_class_name: assetClassName,
      price: parseFloat(orig_price) || 0,
      initial_price: parseFloat(orig_price) || 0,
      add_reason: orig_add_reason,
      isNew: true, // Flag to identify newly added items
    };

    setPortfolioSub((prev) => [...prev, newItem]);

    // Reset form
    setFormData({
      ...formData,
      orig_stocks: "",
      orig_weightage: "",
      orig_segment: "",
      orig_asset_class: "",
      orig_price: "",
      orig_add_reason: "",
    });
  };

  // const handleSubItemChange = (index, field, value) => {
  //   setPortfolioSub((prev) => {
  //     const updated = [...prev];

  //     // Handle different field types
  //     if (field === "weightage" || field === "price") {
  //       updated[index][field] = parseFloat(value) || 0;
  //     } else if (field === "asset_class") {
  //       updated[index][field] = parseInt(value) || 0;
  //       // Update asset class name
  //       const selectedAsset = assetClassList.find(
  //         (asset) => asset.value == value
  //       );
  //       updated[index]["asset_class_name"] = selectedAsset
  //         ? selectedAsset.label
  //         : "";
  //     } else {
  //       updated[index][field] = value;
  //     }

  //     return updated;
  //   });
  // };

  const handleSubItemChange = (index, field, value) => {
    setPortfolioSub((prev) => {
      const updated = [...prev];

      if (field === "weightage" || field === "price") {
        updated[index][field] = parseFloat(value) || 0;
        // Specifically track weightage changes for existing items
        if (field === "weightage" && updated[index].id && updated[index].id !== 0) {
          updated[index].isWeightageEdited = true;
        }
      } else if (field === "asset_class") {
        updated[index][field] = parseInt(value) || 0;
        const selectedAsset = assetClassList.find(
          (asset) => asset.value == value
        );
        updated[index]["asset_class_name"] = selectedAsset
          ? selectedAsset.label
          : "";
      } else {
        updated[index][field] = value;
      }

      // Mark as edited if it’s an existing row (id != 0)
      if (updated[index].id && updated[index].id !== 0) {
        updated[index].isEdited = true;
      }

      return updated;
    });
  };

  const openReasonModal = (index, fieldName) => {
    setCurrentReasonField({ index, fieldName });

    if (fieldName === "add_reason" && index !== null) {
      setReasonText(portfolioSub[index].add_reason || "");
    } else if (fieldName === "orig_add_reason") {
      setReasonText(formData.orig_add_reason || "");
    } else {
      setReasonText("");
    }

    setShowReasonModal(true);
  };

  const saveReason = () => {
    if (currentReasonField) {
      const { index, fieldName } = currentReasonField;

      if (fieldName === "add_reason" && index !== null) {
        handleSubItemChange(index, "add_reason", reasonText);
      } else if (fieldName === "orig_add_reason") {
        setFormData((prev) => ({ ...prev, orig_add_reason: reasonText }));
      }
    }

    setShowReasonModal(false);
    setCurrentReasonField(null);
    setReasonText("");
    showToast.success("Reason saved successfully");
  };

  const handleDeleteRow = async (index, rowId) => {
    if (rowId === 0) {
      // new unsaved row, remove instantly
      setPortfolioSub((prev) => prev.filter((_, i) => i !== index));
      showToast.success("Item removed successfully");
      return;
    }

    const { value: reason } = await Swal.fire({
      title: "Are you sure?",
      text: "Stock will be marked for deletion (not deleted until Push Rebalance)!",
      icon: "warning",
      input: "textarea",
      inputPlaceholder: "Enter reason for deletion...",
      inputValidator: (value) => {
        if (!value || value.trim().length === 0)
          return "You need to provide a reason!";
        if (value.length < 10)
          return "Reason should be at least 10 characters long!";
        return null;
      },
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Mark for Deletion",
      cancelButtonText: "Cancel",
    });

    if (reason) {
      setPendingDelete((prev) => [...prev, { index, rowId, reason }]);
      showToast.info("Stock marked for deletion. Push Rebalance to confirm.");
    }
  };

  const viewDeleteReason = (reason) => {
    setCurrentDeleteReason(reason || "No reason provided");
    setShowDeleteReasonModal(true);
  };

  const handleSubmit = async (e, action) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem("authToken");

      let updatedPortfolio = [...portfolioSub];
      let deleteIds = [];
      let deleteReasonsArr = [];

      if (action === "push_rebalance" && pendingDelete.length > 0) {
        // Apply pending deletes
        pendingDelete.forEach((del) => {
          const item = portfolioSub[del.index];
          deleteIds.push(del.rowId);
          deleteReasonsArr.push(del.reason);

          // Remove from summary
          updatedPortfolio = updatedPortfolio.filter((_, i) => i !== del.index);

          // Move to deleted table
          setDeletedPortfolioSub((prev) => [
            ...prev,
            {
              ...item,
              delete_reason: del.reason,
              delete_date: new Date(),
            },
          ]);
        });
        setPendingDelete([]); // clear after commit
        setPortfolioSub(updatedPortfolio);
      }

      // Prepare payload
      const payload = {
        stocks: updatedPortfolio.map((i) => i.stocks),
        weightage: updatedPortfolio.map((i) => Number(i.weightage || 0)),
        segment: updatedPortfolio.map((i) => i.portfolio_segment || ""),
        asset_class: updatedPortfolio.map((i) => Number(i.asset_class || 0)),
        price: updatedPortfolio.map((i) => Number(i.price || 0)),
        portfoliosubid: updatedPortfolio
          .filter((i) => i.id && i.id !== 0)
          .map((i) => i.id),
        portfoliosub_deleteid: deleteIds,
        portfoliosub_delete_reason: deleteReasonsArr,
        add_reason: updatedPortfolio.map((i) => i.add_reason || ""),
        rebalance_date: formData.rebalance_date,
        submit: action,
      };

      if (
        payload.stocks.length === 0 &&
        payload.portfoliosub_deleteid.length === 0
      ) {
        showToast.error("No changes to save");
        setSaving(false);
        return;
      }

      const response = await axios.post(
        `${API_URL}portfolio/save/${id}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        showToast.success(
          response.data.message || "Portfolio updated successfully!"
        );
        // if (action !== "push_rebalance")
        navigate("/Research/Portfolio/details/" + id);
      } else {
        throw new Error(response.data.message || "Failed to update portfolio");
      }
    } catch (err) {
      console.error("Error saving portfolio:", err);
      showToast.error(
        err.response?.data?.message ||
        err.message ||
        "Failed to update portfolio"
      );
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`; // DD-MM-YYYY format
  };

  if (loading) return <LoadingSpinner />;
  if (!serviceData)
    return <div className="text-center p-5">Portfolio not found</div>;

  // const totalWeightage = portfolioSub.reduce(
  //   (sum, item) => sum + (parseFloat(item.weightage) || 0),
  //   0
  // );

  const totalWeightage = portfolioSub
    .filter(
      (item, index) =>
        !pendingDelete.some(
          (del) => del.index === index || del.rowId === item.id
        )
    )
    .reduce((sum, item) => sum + (parseFloat(item.weightage) || 0), 0);

  return (
    <div
      id="page-container"
      className="page-header-fixed page-sidebar-fixed fade in"
    >
      <div id="content">
        <div className="row pb-2">
          <div className="col-md-6">
            <ul className="breadcrumb">
              <li className="breadcrumb-item">
                <a href="/Dashboard">
                  <FontAwesomeIcon icon={faHome} /> Home
                </a>
              </li>
              <li className="breadcrumb-item active">Manage Portfolio</li>
            </ul>
          </div>
        </div>

        <div className="panel panel-default with-rounded-corner with-shadow">
          <div className="panel-heading">
            <span className="panel-title">
              Edit Portfolio - {serviceData.serviceArr?.service_name}
            </span>
          </div>
          <div className="panel-body">
            <form onSubmit={(e) => handleSubmit(e, "update")}>
              <input type="hidden" name="id" value={id} />

              <div className="row mt-4">
                <div className="col-md-3">
                  <div className="form-group">
                    <label>Rebalance Date</label>
                    <input
                      type="date"
                      name="rebalance_date"
                      className="form-control"
                      value={formData.rebalance_date}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
              </div>

              <table className="table mb-4">
                <tbody>
                  <tr>
                    <th>
                      Last Rebalance Date:{" "}
                      {formatDate(serviceData.serviceArr?.last_rebalance_date)}
                    </th>
                    <th>
                      Next Rebalance Date:{" "}
                      {formatDate(serviceData.serviceArr?.next_rebalance_date)}
                    </th>
                    <th>
                      Minimum Investment:{" "}
                      {formatCurrency(
                        serviceData.serviceArr?.min_investment || 0
                      )}
                    </th>
                  </tr>
                </tbody>
              </table>

              {/* Portfolio add & summary table */}
              <div className="table-responsive col-md-12">
                {/* Add new portfolio row */}
                <table className="table m-b-0">
                  <thead>
                    <tr>
                      <th>Stocks</th>
                      <th>Weightage (%)</th>
                      <th>Segment</th>
                      <th>Asset Class</th>
                      {/* <th>Entry Price</th> */}
                      <th>CMP</th>
                      {/* <th>Change (%)</th> */}
                      <th>Reason for add</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <StateManagedSelect
                          options={symbolList.map((s) => ({
                            value: s,
                            label: s,
                          }))}
                          value={
                            formData.orig_stocks
                              ? {
                                value: formData.orig_stocks,
                                label: formData.orig_stocks,
                              }
                              : null
                          }
                          onChange={(opt) => handleStockSelect(opt?.value)}
                          placeholder="Select Stock"
                          isSearchable
                          isClearable
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          name="orig_weightage"
                          className="form-control"
                          value={formData.orig_weightage}
                          onChange={handleInputChange}
                          step="0.01"
                          min="0"
                          max="100"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          name="orig_segment"
                          className="form-control"
                          value={formData.orig_segment}
                          onChange={handleInputChange}
                          placeholder="e.g., Technology, Healthcare"
                        />
                      </td>
                      <td>
                        <select
                          name="orig_asset_class"
                          className="form-control"
                          value={formData.orig_asset_class}
                          onChange={handleInputChange}
                        >
                          <option value="">Select Asset Class</option>
                          {assetClassList.map((asset) => (
                            <option key={asset.value} value={asset.value}>
                              {asset.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      {/* <td>
                        <input
                          type="number"
                          name="orig_initial_price"
                          className="form-control"
                          value={formData.orig_initial_price || ""}
                          onChange={handleInputChange}
                          step="0.01"
                          min="0"
                          disabled={true}
                        />
                      </td> */}
                      <td>
                        <input
                          type="number"
                          name="orig_price"
                          className="form-control"
                          value={formData.orig_price}
                          onChange={handleInputChange}
                          step="0.01"
                          min="0"
                        />
                      </td>
                      {/*<td>
                        <input
                          type="text"
                          name="orig_change_percent"
                          className="form-control"
                          value={formData.orig_change_percent || ""}
                          onChange={handleInputChange}
                          disabled={true}
                        />
                      </td> */}
                      <td>
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={() =>
                            openReasonModal(null, "orig_add_reason")
                          }
                          title="Add Reason"
                        >
                          <FontAwesomeIcon icon={faPlus} />
                        </button>
                        {formData.orig_add_reason && (
                          <small className="d-block text-muted mt-1">
                            Reason added
                          </small>
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-success"
                          onClick={addPortfolioItem}
                          // disabled={!formData.orig_stocks || !formData.orig_weightage}
                          disabled={!formData.orig_add_reason} // <-- Enable
                        >
                          <FontAwesomeIcon icon={faPlus} /> Add Data
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>

                {/* Summary Table */}
                <table width="100%" className="table mt-2">
                  <thead>
                    <tr align="center">
                      <th colSpan="9">Summary</th>
                    </tr>
                    <tr align="center">
                      <th>Stocks</th>
                      <th>Weightage (%)</th>
                      <th>Segment</th>
                      <th>Asset Class</th>
                      <th>Entry Price</th>
                      <th>CMP</th>
                      <th>Change (%)</th>
                      <th>Reason for add</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {portfolioSub.length ? (
                      <>
                        {portfolioSub.map((item, index) => {
                          const isPendingDelete = pendingDelete.some(
                            (del) =>
                              del.index === index || del.rowId === item.id
                          );

                          return (
                            <tr
                              key={index}
                              align="center"
                              style={
                                isPendingDelete
                                  ? { opacity: 0.6, backgroundColor: "#f8d7da" }
                                  : {}
                              }
                            >
                              <td className="font-weight-bold">
                                {item.stocks}
                              </td>

                              <td>
                                <input
                                  type="number"
                                  className="form-control text-center"
                                  value={item.weightage}
                                  onChange={(e) =>
                                    !isPendingDelete &&
                                    handleSubItemChange(
                                      index,
                                      "weightage",
                                      e.target.value
                                    )
                                  }
                                  step="0.01"
                                  min="0"
                                  max="100"
                                  disabled={isPendingDelete}
                                />
                              </td>

                              <td>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={item.portfolio_segment || ""}
                                  onChange={(e) =>
                                    !isPendingDelete &&
                                    handleSubItemChange(
                                      index,
                                      "portfolio_segment",
                                      e.target.value
                                    )
                                  }
                                  disabled={isPendingDelete}
                                />
                              </td>

                              <td>
                                <select
                                  className="form-control"
                                  value={item.asset_class || 0}
                                  onChange={(e) =>
                                    !isPendingDelete &&
                                    handleSubItemChange(
                                      index,
                                      "asset_class",
                                      e.target.value
                                    )
                                  }
                                  disabled={isPendingDelete}
                                >
                                  {assetClassList.map((asset) => (
                                    <option
                                      key={asset.value}
                                      value={asset.value}
                                    >
                                      {asset.label}
                                    </option>
                                  ))}
                                </select>
                              </td>

                              <td>
                                <input
                                  type="number"
                                  className="form-control text-center"
                                  value={item.initial_price || 0}
                                  disabled={true}
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  className="form-control text-center"
                                  value={item.price || 0}
                                  disabled={true}
                                />
                              </td>
                              <td
                                style={{
                                  color:
                                    parseFloat(item.change_percent) >= 0
                                      ? "green"
                                      : "red",
                                  fontWeight: "bold",
                                  verticalAlign: "middle",
                                }}
                              >
                                {item.change_percent
                                  ? `${item.change_percent}%`
                                  : "0.00%"}
                              </td>

                              <td>
                                <button
                                  type="button"
                                  className="btn btn-primary btn-sm"
                                  onClick={() =>
                                    !isPendingDelete &&
                                    openReasonModal(index, "add_reason")
                                  }
                                  title={
                                    item.add_reason
                                      ? "Edit Reason"
                                      : "Add Reason"
                                  }
                                  disabled={isPendingDelete}
                                >
                                  <FontAwesomeIcon icon={faPlus} />
                                </button>
                              </td>

                              <td>
                                <button
                                  type="button"
                                  className="btn btn-danger btn-sm"
                                  onClick={() =>
                                    !isPendingDelete &&
                                    handleDeleteRow(index, item.id)
                                  }
                                  title="Delete Stock"
                                  disabled={isPendingDelete}
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                        <tr
                          style={{
                            backgroundColor: "#f4f4f4",
                            fontWeight: "bold",
                          }}
                        >
                          <td align="center">Total Weightage</td>
                          <td
                            align="center"
                            style={{
                              color: totalWeightage === 100 ? "green" : "red",
                              fontSize: "14px",
                            }}
                          >
                            {totalWeightage.toFixed(2)}%
                          </td>
                          <td colSpan="7"></td>
                        </tr>
                      </>
                    ) : (
                      <tr>
                        <td colSpan="9" className="text-center p-3">
                          No items added yet
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* <div className="row mt-4">
                <div className="col-lg-12 d-flex gap-2">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    // disabled={
                    //   saving ||
                    //   portfolioSub.length === 0 ||
                    //   pendingDelete.length > 0 ||
                    //   portfolioSub.some((i) => i.id === 0 || i.isNew) // disable if new stock exists
                    // }
                    disabled={
                      saving ||
                      (portfolioSub.length === 0 &&
                        pendingDelete.length === 0) ||
                      (!portfolioSub.some(
                        (i) => i.id === 0 || i.isNew || i.isEdited
                      ) &&
                        pendingDelete.length === 0)
                    }
                  >
                    {saving ? "Updating..." : "Update"}
                  </button>

                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={(e) => handleSubmit(e, "skip")}
                    disabled={
                      saving ||
                      pendingDelete.length > 0 ||
                      portfolioSub.some((i) => i.id === 0 || i.isNew) // disable if new stock exists
                    }
                  >
                    Skip
                  </button>

                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={(e) => handleSubmit(e, "push_rebalance")}
                    disabled={
                      saving ||
                      (pendingDelete.length === 0 &&
                        !portfolioSub.some((i) => i.id === 0 || i.isNew)) // enable only if delete/new exists
                    }
                  >
                    {saving ? "Pushing..." : "Push Rebalance"}
                  </button>

                  <Link to={`/Research/Portfolio`} className="btn btn-danger">
                    Cancel
                  </Link>
                </div>
              </div> */}
              <div className="row mt-4">
                <div className="col-lg-12 d-flex gap-2">
                  {/* ✅ Update Button */}
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={
                      saving ||
                      (portfolioSub.length === 0 &&
                        pendingDelete.length === 0) ||
                      (!portfolioSub.some(
                        (i) => i.isEdited && i.id !== 0 // only allow update if an existing stock is edited
                      ) &&
                        pendingDelete.length === 0) ||
                      portfolioSub.some((i) => i.isNew || i.id === 0) || // disable if new stock exists
                      totalWeightage !== 100 // must be 100 before update
                    }
                  >
                    {saving ? "Updating..." : "Update"}
                  </button>

                  {/* ✅ Skip Button */}
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={(e) => handleSubmit(e, "skip")}
                    disabled={
                      saving ||
                      pendingDelete.length > 0 ||
                      portfolioSub.some((i) => i.isNew || i.id === 0) || // disable if new stock exists
                      totalWeightage !== 100
                    }
                  >
                    Skip
                  </button>

                  {/* ✅ Push Rebalance Button */}
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={(e) => handleSubmit(e, "push_rebalance")}
                    disabled={
                      saving ||
                      totalWeightage !== 100 ||
                      (pendingDelete.length === 0 &&
                        !portfolioSub.some((i) => i.isNew || i.id === 0 || i.isWeightageEdited)) // enable if weightage update, new or pending delete
                    }
                  >
                    {saving ? "Pushing..." : "Push Rebalance"}
                  </button>

                  <Link to={`/Research/Portfolio`} className="btn btn-danger">
                    Cancel
                  </Link>
                </div>
              </div>
            </form>

            {/* Deleted Stocks Table */}
            {deletedPortfolioSub.length > 0 && (
              <div className="table-responsive col-md-10 mt-4  mx-auto">
                <h5 className="text-center">Deleted Stocks</h5>
                <hr />
                <table className="table m-b-0 col-12">
                  <thead>
                    <tr>
                      <th style={{ textAlign: "center", width: "15%" }}>
                        Stocks
                      </th>
                      <th style={{ textAlign: "center", width: "10%" }}>
                        Weightage (%)
                      </th>
                      <th style={{ textAlign: "center", width: "15%" }}>
                        Segment
                      </th>
                      <th style={{ textAlign: "center", width: "15%" }}>
                        Asset Class
                      </th>
                      <th style={{ textAlign: "center", width: "10%" }}>
                        Entry Price
                      </th>
                      <th style={{ textAlign: "center", width: "10%" }}>
                        CMP
                      </th>
                      <th style={{ textAlign: "center", width: "10%" }}>
                        Change (%)
                      </th>
                      <th style={{ textAlign: "center", width: "15%" }}>
                        Reason for Delete
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {deletedPortfolioSub.map((item, index) => (
                      <tr key={index} align="center">
                        <td
                          style={{ verticalAlign: "middle" }}
                          className="font-weight-bold"
                        >
                          {item.stocks}
                        </td>
                        <td style={{ verticalAlign: "middle" }}>
                          {item.weightage}
                        </td>
                        <td style={{ verticalAlign: "middle" }}>
                          {item.portfolio_segment}
                        </td>
                        <td style={{ verticalAlign: "middle" }}>
                          {assetClassList.find(
                            (asset) => asset.value == item.asset_class
                          )?.label || item.asset_class}
                        </td>
                        <td style={{ verticalAlign: "middle" }}>
                          {item.initial_price || "0.00"}
                        </td>
                        <td style={{ verticalAlign: "middle" }}>
                          {item.last_price || item.price || "0.00"}
                        </td>
                        <td
                          style={{
                            verticalAlign: "middle",
                            color: parseFloat(item.change_percent) >= 0 ? "green" : "red",
                            fontWeight: "bold",
                          }}
                        >
                          {item.change_percent ? `${item.change_percent}%` : "0.00%"}
                        </td>
                        <td style={{ verticalAlign: "middle" }}>
                          <button
                            className="btn btn-sm btn-info"
                            type="button"
                            onClick={() => viewDeleteReason(item.delete_reason)}
                          >
                            <FontAwesomeIcon icon={faEye} /> View Reason
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Reason Modal */}
        {showReasonModal && (
          <div
            className="modal fade show"
            style={{
              display: "block",
              backgroundColor: "rgba(0, 0, 0, 0.5)", // backdrop
              zIndex: 1050, // above page
            }}
          >
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header d-flex justify-content-between">
                  <h5 className="modal-title">Enter Reason</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setShowReasonModal(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <textarea
                    className="form-control"
                    rows="6"
                    value={reasonText}
                    onChange={(e) => setReasonText(e.target.value)}
                    placeholder="Enter reason here..."
                    ref={reasonInputRef}
                  ></textarea>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowReasonModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={saveReason}
                  >
                    Save Reason
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Reason Modal */}
        {showDeleteReasonModal && (
          <div
            className="modal fade show"
            style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header d-flex justify-content-between">
                  <h5 className="modal-title">Delete Reason</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowDeleteReasonModal(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <div
                    dangerouslySetInnerHTML={{ __html: currentDeleteReason }}
                  />
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowDeleteReasonModal(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <a href="#" data-click="scroll-top" className="btn-scroll-top fade">
          <FontAwesomeIcon icon={faArrowUp} />
        </a>
      </div>
    </div>
  );
};

export default PortfolioEdit;
